/**
 * Date Utilities
 * -----------------------------------------------------------------------------
 * This module provides a set of utility functions for strict ISO date handling,
 * comparison, and manipulation. Each function operates with `yyyy-mm-dd`-formatted
 * date strings to ensure consistent and predictable behavior across the application.
 *
 * Included Utilities:
 * - ISO_YMD_STRICT: Regex pattern for strict ISO date validation.
 * - stripToYmdStrict: Safely extracts a strict ISO date substring from input.
 * - addYears: Adds a specified number of years to a given date string.
 * - isIsoDateInRange: Validates if a date falls within a start/end range.
 * - compareIsoAsc: Ascending ISO date comparator.
 * - compareIsoDesc: Descending ISO date comparator.
 * - toTime: Internal helper for safe timestamp conversion.
 *
 * Notes:
 * - All functions handle nullish and malformed inputs gracefully.
 * - These helpers are intended for frontend and backend-safe ISO operations,
 *   such as sorting, filtering, and form validation.
 * -----------------------------------------------------------------------------
 */

/**
 * Strict ISO date regex pattern (`yyyy-mm-dd`).
 *
 * Matches a 4-digit year, 2-digit month (01–12), and 2-digit day (01–31),
 * followed by any optional trailing characters (which are ignored by the capture group).
 * The capturing group (m[1]) isolates just the date portion.
 *
 * Examples of matches:
 * - "2025-10-30" (valid)
 * - "2025-02-09T12:00:00Z" (captures "2025-02-09")
 * - "2025-2-9" (fails, month/day must be 2 digits)
 */
const ISO_YMD_STRICT =
    /^(\d{4}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12]\d|3[01])).*$/;

/**
 * Strips a string down to a strictly formatted ISO date (`yyyy-mm-dd`).
 *
 * Ensures the input matches a strict ISO date pattern using `ISO_YMD_STRICT`.
 * If the input does not match, it returns an empty string.
 * Useful for sanitizing user-entered or loosely formatted date strings before
 * assigning them to controlled form fields or serializing for APIs.
 *
 * @param {string | null | undefined} s - The input string that may contain a date.
 *     If `s` is `null`, `undefined`, or not in ISO format, it is treated as an empty string.
 * @returns {string} The matched date in `yyyy-mm-dd` format, or an empty string if not matched.
 *
 * @example
 * // Returns a valid ISO date
 * stripToYmdStrict('2025-10-30'); // '2025-10-30'
 *
 * // Returns empty string for invalid input
 * stripToYmdStrict('10/30/2025'); // ''
 *
 * // Handles nullish input safely
 * stripToYmdStrict(null); // ''
 * stripToYmdStrict(undefined); // ''
 * stripToYmdStrict(''); // ''
 */
export const stripToYmdStrict = (s?: string | null): string => {
    const v = (s ?? '').trim();

    // Matches the string against the strict ISO pattern.
    // m[1] will contain only the "yyyy-mm-dd" portion if valid.
    const m = v.match(ISO_YMD_STRICT);

    return m ? m[1] : '';
};

/**
 * Adds a given number of years to a date string formatted as `yyyy-mm-dd`.
 *
 * Parses the provided date string into a JavaScript `Date` object,
 * adjusts its full year by the specified number, and then returns
 * the result in normalized `yyyy-mm-dd` format.
 *
 * This function preserves the original month and day, correctly
 * handling leap years and end-of-month overflows based on JavaScript's
 * date arithmetic (e.g., adding one year to `2020-02-29` will yield `2021-03-01`).
 *
 * @param {string} dateStr - The original date string to modify, expected in `yyyy-mm-dd` format.
 * @param {number} years - The number of years to add (can be positive or negative).
 * @returns {string} A new date string in `yyyy-mm-dd` format with the added years applied.
 *
 * @example
 * // Basic usage
 * addYears('2020-10-30', 1); // '2021-10-30'
 *
 * // Handles leap years gracefully
 * addYears('2020-02-29', 1); // '2021-03-01'
 *
 * // Works with negative offsets
 * addYears('2025-10-30', -5); // '2020-10-30'
 */
export const addYears = (dateStr: string, years: number): string => {
    // Split the date string into year, month, and day components.
    // The 'mm' is 1-based, so we subtract 1 when constructing the Date object.
    const [yyyy, mm, dd] = dateStr.split('-').map(Number);

    // Create a Date object based on the given components.
    const date = new Date(yyyy, mm - 1, dd);

    // Add the specified number of years.
    date.setFullYear(date.getFullYear() + years);

    // Reformat the updated date back to a zero-padded yyyy-mm-dd string.
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');

    return `${y}-${m}-${d}`;
};

/**
 * Checks whether an ISO 8601 date string falls within a specified start/end range.
 *
 * Converts the target date, start, and end dates into epoch timestamps and
 * compares them numerically. Handles null/undefined inputs gracefully by
 * treating them as open-ended bounds.
 *
 * @param {string | null | undefined} targetDate - The ISO date string to check (e.g., '2025-10-30').
 *     If null or undefined, the function immediately returns `false`.
 * @param {string | null | undefined} [dateStart] - Optional start date in ISO format.
 *     If omitted, the lower bound defaults to 0 (effectively no lower limit).
 * @param {string | null | undefined} [dateEnd] - Optional end date in ISO format.
 *     If omitted, the upper bound defaults to positive infinity (no upper limit).
 * @returns {boolean} True if `targetDate` lies inclusively between `dateStart` and `dateEnd`; false otherwise.
 *
 * @example
 * // Returns true
 * isIsoDateInRange('2025-10-30', '2025-10-01', '2025-11-01'); // true
 *
 * // Returns false (before range)
 * isIsoDateInRange('2025-09-30', '2025-10-01', '2025-11-01'); // false
 *
 * // Returns true (open-ended start)
 * isIsoDateInRange('2025-10-30', null, '2025-12-31'); // true
 */
export const isIsoDateInRange = (
    targetDate: string | null | undefined,
    dateStart?: string | null,
    dateEnd?: string | null
): boolean => {
    // Return false early if no target date was provided.
    if (!targetDate || targetDate === undefined) {
        return false;
    }

    // Convert ISO strings into epoch timestamps (milliseconds since 1970-01-01 UTC).
    const targetEpoch = Date.parse(targetDate);
    const startEpoch = dateStart ? Date.parse(dateStart) : 0;
    const endEpoch = dateEnd ? Date.parse(dateEnd) : Number.POSITIVE_INFINITY;

    // Inclusive range check
    return targetEpoch >= startEpoch && targetEpoch <= endEpoch;
};

/**
 * Compares two ISO date strings in ascending order.
 *
 * Converts both input strings to epoch timestamps and returns the numeric difference.
 * Invalid or empty date strings are treated as NaN and sorted to the end.
 *
 * @param {string | undefined} [a] - The first ISO date string (e.g., '2025-10-30').
 * @param {string | undefined} [b] - The second ISO date string.
 * @returns {number} Negative if `a` < `b`, positive if `a` > `b`, or 0 if equal.
 *
 * @example
 * compareIsoAsc('2025-10-01', '2025-11-01'); // -1
 * compareIsoAsc('2025-11-01', '2025-10-01'); // 1
 * compareIsoAsc('2025-10-01', '2025-10-01'); // 0
 *
 * // Invalid/empty strings are considered last
 * compareIsoAsc('', '2025-10-01'); // 1
 */
export const compareIsoAsc = (a?: string, b?: string): number => {
    const ta = toTime(a);
    const tb = toTime(b);

    // If both are invalid, treat as equal.
    if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;

    // Invalid or empty 'a' sorts after valid dates.
    if (Number.isNaN(ta)) return 1;

    // Invalid or empty 'b' sorts after valid 'a'.
    if (Number.isNaN(tb)) return -1;

    // Return ascending difference.
    return ta - tb;
};

/**
 * Compares two ISO date strings in descending order.
 *
 * Identical logic to `compareIsoAsc` but reverses the comparison direction.
 * Invalid or empty date strings are still sorted last (lowest priority).
 *
 * @param {string | undefined} [a] - The first ISO date string (e.g., '2025-10-30').
 * @param {string | undefined} [b] - The second ISO date string.
 * @returns {number} Negative if `a` > `b`, positive if `a` < `b`, or 0 if equal.
 *
 * @example
 * compareIsoDesc('2025-11-01', '2025-10-01'); // -1
 * compareIsoDesc('2025-10-01', '2025-11-01'); // 1
 * compareIsoDesc('2025-10-01', '2025-10-01'); // 0
 *
 * // Invalid/empty strings are considered last
 * compareIsoDesc('', '2025-10-01'); // 1
 */
export const compareIsoDesc = (a?: string, b?: string): number => {
    const ta = toTime(a);
    const tb = toTime(b);

    // Both invalid → equal
    if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;

    // Invalid/empty 'a' goes last
    if (Number.isNaN(ta)) return 1;

    // Invalid/empty 'b' goes last
    if (Number.isNaN(tb)) return -1;

    // Reverse order difference for descending sort
    return tb - ta;
};

/**
 * Converts an ISO date string into a timestamp (epoch milliseconds).
 *
 * Acts as a safe wrapper around `Date.parse()` with NaN fallback for invalid or empty inputs.
 * Used internally by comparison helpers (`compareIsoAsc`, `compareIsoDesc`) to ensure consistent
 * date parsing behavior.
 *
 * @param {string | undefined} [s] - The date string to parse.
 * @returns {number} The numeric epoch value, or NaN if input is invalid or blank.
 *
 * @example
 * toTime('2025-10-30'); // 1761782400000 (epoch ms)
 * toTime(''); // NaN
 * toTime(undefined); // NaN
 */
const toTime = (s?: string): number => {
    // Treat empty or whitespace-only strings as invalid
    if (!s || !s.trim()) return NaN;

    // Parse as ISO date, returning NaN if parsing fails
    return Date.parse(s);
};
